import { useTranslation } from "react-i18next";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { describeError, useToast } from "../toast";
import { useFormat, type Formatters } from "../../i18n/format";
import { translateKey, type Translate } from "../../i18n/translate";

/**
 * The PMS's catalogue on Promotions → Offers (`MEWS-3`): which of the vendor's
 * outlets feed the TV, "Sync now", and how the last import went. It lived on
 * the connection's card under PMS → Integrations until 2026-10-28; it sits with
 * the offers now, so the hotel picks and syncs its own services without Nenda
 * (`PmsOffersImport` finds the connection).
 *
 * The services are a search-as-you-type picker, not a list of checkboxes
 * (Sigge, 2026-10-29): a PMS can list dozens of services, of which a hotel
 * feeds the TV two or three, and a wall of checkboxes buried the offers below.
 *
 * Vendor-neutral. The outlet picker appears for any config field the adapter
 * declares as `kind: "outlets"`; the status appears for any adapter that reads
 * a menu. Nothing here names Mews.
 *
 * Two things it says out loud rather than leave to be assumed:
 * - **Imported offers arrive switched OFF.** Going live is a switch in the
 *   offers list, never a side effect of a sync.
 * - **What could not be placed.** "3 items I couldn't place" with the reason,
 *   never a quietly shorter menu — the `menuFeed` discipline.
 */

export interface OutletField {
  key: string;
  label: string;
  help?: string;
}

interface Outlet {
  outletRef: string;
  name: string;
  currency: string | null;
}

interface OutletStatus {
  outletRef: string;
  offerId: string | null;
  detached: boolean;
  itemCount: number;
  unplaced: Array<{ vendorItemRef: string | null; raw: string; why: string }>;
  heldCode: string | null;
  lastOkAt: string | null;
}

interface MenuStatus {
  lastSyncAt: string | null;
  lastOkAt: string | null;
  lastErrorCode: string | null;
  outlets: OutletStatus[];
}

interface Props {
  premiseId: string;
  connectionId: string;
  vendor: string;
  displayName: string;
  /** The saved outlet refs, comma-separated (the `kind: "outlets"` field). */
  picked: string;
  hasCredentials: boolean;
  /** May pick services and sync (`offers` edit). */
  canEdit: boolean;
  readMenu: boolean;
  outletField: OutletField | null;
  /** Turns a refusal code into a sentence; the panel owns that table. */
  refusalCopy: (code: string | null) => string;
  /** Called after the picked services changed, so the card reloads. */
  onSaved: () => Promise<void>;
  /** Called after a sync, so the offers list shows what it imported. */
  onSynced?: () => void;
}

const SYNC_COPY: Record<string, string> = {
  not_due: "linkCatalogueSection.syncCodes.notDue",
  connection_disabled: "linkCatalogueSection.syncCodes.connectionDisabled",
  empty_catalogue_held: "linkCatalogueSection.syncCodes.emptyCatalogueHeld",
  internal: "linkCatalogueSection.syncCodes.internal",
};

function syncCopy(t: Translate, code: string | null): string | undefined {
  const key = SYNC_COPY[code ?? ""];
  return key ? translateKey(t, `premises:${key}`) : undefined;
}

function parseRefs(raw: string | undefined): string[] {
  return (raw ?? "")
    .split(/[\s,;]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Why a line could not be placed, in the operator's language. The server says
 * it in English for its own logs; the known reasons are translated here and an
 * unknown one is shown as sent.
 */
function unplacedWhy(t: Translate, why: string): string {
  const patterns: Array<
    [RegExp, string, (m: RegExpExecArray) => Record<string, string>]
  > = [
    [
      /^priced in (\S+), not (\S+)$/,
      "priced",
      (m) => ({ currency: m[1]!, expected: m[2]! }),
    ],
    [/^no name in (.+)$/, "noNameIn", (m) => ({ name: m[1]! })],
    [
      /^a tax or fee in (.+), not something a guest orders$/,
      "taxOrFee",
      (m) => ({ name: m[1]! }),
    ],
    [
      /^the catalogue was cut off; select fewer (.+) services$/,
      "cutOff",
      (m) => ({ name: m[1]! }),
    ],
  ];
  for (const [re, key, args] of patterns) {
    const m = re.exec(why);
    if (m) return t(`linkCatalogueSection.why.${key}`, args(m));
  }
  const exact: Record<string, string> = {
    "no stable item reference": "noRef",
    "no name": "noName",
    "no price": "noPrice",
    "no price and no category": "noPriceNoCategory",
  };
  return exact[why] ? t(`linkCatalogueSection.why.${exact[why]}`) : why;
}

function when(t: Translate, fmt: Formatters, iso: string | null): string {
  return iso ? fmt.dateTime(iso) : t("premises:linkCatalogueSection.never");
}

export default function LinkCatalogueSection(props: Props) {
  const {
    premiseId,
    connectionId,
    vendor,
    displayName,
    picked: pickedRaw,
    hasCredentials,
    canEdit,
    readMenu,
    outletField,
    refusalCopy,
    onSaved,
    onSynced,
  } = props;
  const toast = useToast();
  const { t } = useTranslation(["premises", "common"]);
  const fmt = useFormat();
  const base = `/api/cms/v1/premise/${premiseId}/link/connections/${connectionId}`;

  const [outlets, setOutlets] = useState<Outlet[] | null>(null);
  const [outletError, setOutletError] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[]>(parseRefs(pickedRaw));
  const [status, setStatus] = useState<MenuStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [openUnplaced, setOpenUnplaced] = useState<string | null>(null);

  const saved = useMemo(() => parseRefs(pickedRaw), [pickedRaw]);
  const dirty =
    picked.length !== saved.length || picked.some((r) => !saved.includes(r));

  const loadOutlets = useCallback(async () => {
    if (!outletField || !hasCredentials) return;
    setOutletError(null);
    try {
      const res = await fetch(`${base}/outlets`, { credentials: "include" });
      const body = (await res.json()) as {
        ok: boolean;
        code: string | null;
        outlets: Outlet[];
      };
      if (!body.ok) {
        setOutlets([]);
        setOutletError(
          refusalCopy(body.code) || t("linkCatalogueSection.listFailed")
        );
        return;
      }
      setOutlets(body.outlets);
    } catch {
      setOutlets([]);
      setOutletError(t("linkCatalogueSection.serverUnreachable"));
    }
  }, [base, outletField, hasCredentials, refusalCopy, t]);

  const loadStatus = useCallback(async () => {
    if (!readMenu) return;
    try {
      const res = await fetch(`${base}/menu`, { credentials: "include" });
      if (res.ok) setStatus((await res.json()) as MenuStatus);
    } catch {
      // The status is a convenience; the picker still works without it.
    }
  }, [base, readMenu]);

  useEffect(() => {
    void loadOutlets();
    void loadStatus();
  }, [loadOutlets, loadStatus]);

  useEffect(() => setPicked(saved), [saved]);

  async function savePicked() {
    if (!outletField) return;
    setBusy(true);
    try {
      // Its own route: it rewrites the picked services and nothing else of
      // the connection, which is Nenda's.
      const res = await fetch(`${base}/outlets`, {
        method: "PUT",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ outletRefs: picked }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { dropped = 0 } = (await res.json()) as { dropped?: number };
      const removed = dropped
        ? ` ${t("linkCatalogueSection.offersRemoved", { count: dropped })}`
        : "";
      toast.success(
        (picked.length === 0
          ? t("linkCatalogueSection.noServices", { name: displayName })
          : t("linkCatalogueSection.savedSyncHint", { count: picked.length })) +
          removed
      );
      await onSaved();
      // An unticked service's offer was deleted: the list below reloads.
      if (dropped) {
        await loadStatus();
        onSynced?.();
      }
    } catch (e) {
      toast.error(describeError(t, "saveLinkServices", e));
    } finally {
      setBusy(false);
    }
  }

  async function syncNow() {
    setBusy(true);
    try {
      const res = await fetch(`${base}/menu/sync`, {
        method: "POST",
        credentials: "include",
      });
      const body = (await res.json()) as {
        ok: boolean;
        code: string | null;
        created: number;
        updated: number;
        held: number;
      };
      if (body.ok) {
        const parts = [
          body.created
            ? t("linkCatalogueSection.syncParts.created", {
                count: body.created,
              })
            : "",
          body.updated
            ? t("linkCatalogueSection.syncParts.updated", {
                count: body.updated,
              })
            : "",
          body.held
            ? t("linkCatalogueSection.syncParts.held", { count: body.held })
            : "",
        ].filter(Boolean);
        toast.success(
          parts.length
            ? t("linkCatalogueSection.synced", { parts: parts.join(", ") })
            : t("linkCatalogueSection.syncedNoChange")
        );
      } else {
        toast.error(
          syncCopy(t, body.code) ??
            (refusalCopy(body.code) || t("linkCatalogueSection.syncFailed"))
        );
      }
      await loadStatus();
      onSynced?.();
    } catch (e) {
      toast.error(describeError(t, "syncLinkMenu", e));
    } finally {
      setBusy(false);
    }
  }

  if (!outletField && !readMenu) return null;

  const nameOf = (ref: string) =>
    outlets?.find((o) => o.outletRef === ref)?.name ?? ref;
  // Services ticked when the page loaded, unticked since: saving deletes their
  // offers (Sigge, 2026-10-30), so say so before the click.
  const unticking = saved.filter(
    (r) =>
      !picked.includes(r) &&
      (status?.outlets ?? []).some((o) => o.outletRef === r && o.offerId)
  ).length;

  return (
    <Stack spacing={1.5} data-testid={`link-catalogue-${vendor}`}>
      {outletField && (
        <Box>
          {/* Our own words, not the adapter's: the server's label and help
              are English, and every vendor's services field means the same. */}
          <Typography variant="subtitle2">
            {t("linkCatalogueSection.outletsLabel", { name: displayName })}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="p">
            {t("linkCatalogueSection.outletsHelp")}
          </Typography>

          {!hasCredentials ? (
            <Typography variant="body2" color="text.secondary">
              {t("linkCatalogueSection.credentialsFirst", {
                name: displayName,
              })}
            </Typography>
          ) : outlets === null ? (
            <CircularProgress size={18} />
          ) : (
            <>
              {outletError && <Alert severity="warning">{outletError}</Alert>}
              {outlets.length === 0 && !outletError && (
                <Typography variant="body2" color="text.secondary">
                  {t("linkCatalogueSection.noActiveServices", {
                    name: displayName,
                  })}
                </Typography>
              )}
              {outlets.length > 0 && (
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  size="small"
                  disabled={!canEdit}
                  options={outlets}
                  value={outlets.filter((o) => picked.includes(o.outletRef))}
                  onChange={(_, next) =>
                    setPicked(next.map((o) => o.outletRef))
                  }
                  isOptionEqualToValue={(a, b) => a.outletRef === b.outletRef}
                  getOptionLabel={(o) => o.name}
                  renderOption={(liProps, o, { selected }) => (
                    <li {...liProps} key={o.outletRef}>
                      <Checkbox
                        size="small"
                        checked={selected}
                        sx={{ mr: 1 }}
                      />
                      {o.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        picked.length === 0
                          ? t("linkCatalogueSection.searchServices", {
                              count: outlets.length,
                              name: displayName,
                            })
                          : t("linkCatalogueSection.addAnother")
                      }
                    />
                  )}
                  sx={{ mt: 1, maxWidth: 640 }}
                  data-testid="link-outlets"
                />
              )}
              {/* A ref saved earlier that the vendor no longer lists is SHOWN:
                  silently dropping it would hide a service that vanished. */}
              {saved
                .filter((r) => !outlets.some((o) => o.outletRef === r))
                .map((r) => (
                  <Alert key={r} severity="warning" sx={{ mt: 1 }}>
                    {t("linkCatalogueSection.serviceGone", {
                      ref: r,
                      name: displayName,
                    })}
                  </Alert>
                ))}
              {unticking > 0 && (
                <Alert
                  severity="warning"
                  sx={{ mt: 1 }}
                  data-testid="link-outlets-unticking"
                >
                  {t("linkCatalogueSection.untickWarning", {
                    count: unticking,
                  })}
                </Alert>
              )}
              <Button
                size="small"
                variant="outlined"
                disabled={busy || !dirty || !canEdit}
                onClick={() => void savePicked()}
                data-testid="link-outlets-save"
                sx={{ mt: 1 }}
              >
                {t("linkCatalogueSection.saveServices")}
              </Button>
            </>
          )}
        </Box>
      )}

      {readMenu && (
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2">
              {t("linkCatalogueSection.importedOffers")}
            </Typography>
            <Button
              size="small"
              disabled={
                busy || !canEdit || !hasCredentials || saved.length === 0
              }
              onClick={() => void syncNow()}
              data-testid="link-sync-now"
            >
              {t("linkCatalogueSection.syncNow")}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" component="p">
            {t("linkCatalogueSection.refreshNote", {
              when: when(t, fmt, status?.lastSyncAt ?? null),
            })}
          </Typography>

          {status?.lastErrorCode && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {syncCopy(t, status.lastErrorCode) ??
                refusalCopy(status.lastErrorCode)}
            </Alert>
          )}

          {(status?.outlets ?? []).map((o) => (
            <Box
              key={o.outletRef}
              sx={{ mt: 1 }}
              data-testid={`link-outlet-${o.outletRef}`}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <Typography variant="body2">{nameOf(o.outletRef)}</Typography>
                {o.offerId && (
                  <Chip
                    size="small"
                    label={t("linkCatalogueSection.itemCount", {
                      count: o.itemCount,
                    })}
                  />
                )}
                {o.detached && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t("linkCatalogueSection.offerDeleted")}
                  />
                )}
                {!o.offerId && !o.detached && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t("linkCatalogueSection.nothingToImport")}
                  />
                )}
                {o.heldCode && (
                  <Chip
                    size="small"
                    color="warning"
                    label={
                      SYNC_COPY[o.heldCode]
                        ? t("linkCatalogueSection.held")
                        : o.heldCode
                    }
                  />
                )}
                {o.unplaced.length > 0 && (
                  <Button
                    size="small"
                    color="warning"
                    onClick={() =>
                      setOpenUnplaced(
                        openUnplaced === o.outletRef ? null : o.outletRef
                      )
                    }
                  >
                    {t("linkCatalogueSection.unplaced", {
                      count: o.unplaced.length,
                    })}
                  </Button>
                )}
              </Stack>
              {o.heldCode && SYNC_COPY[o.heldCode] && (
                <Typography variant="caption" color="warning.main">
                  {syncCopy(t, o.heldCode)}
                </Typography>
              )}
              <Collapse in={openUnplaced === o.outletRef}>
                <Box component="ul" sx={{ m: 0, pl: 3 }}>
                  {o.unplaced.map((u, i) => (
                    <Typography
                      component="li"
                      variant="caption"
                      key={`${u.vendorItemRef}-${i}`}
                    >
                      {u.raw}: {unplacedWhy(t, u.why)}
                    </Typography>
                  ))}
                </Box>
              </Collapse>
            </Box>
          ))}
        </Box>
      )}
    </Stack>
  );
}
